import React from 'react';
import { Link } from 'react-router-dom';

/**
 * RanchWorks Logo Component
 * 
 * Variants:
 * - full: RW mark + RanchWorks text + optional tagline
 * - compact: RW mark + RanchWorks text (no tagline)
 * - mark: RW brand mark only (for app icons)
 * 
 * Sizes:
 * - sm: Small (header navigation)
 * - md: Medium (default)
 * - lg: Large (hero sections, landing pages)
 */

export function Logo({ 
  variant = 'compact', 
  size = 'md', 
  showTagline = false,
  className = '',
  linkTo = '/',
  onClick = null
}) {
  // Size configurations
  const sizes = {
    sm: {
      mark: 'w-7 h-7',
      markText: 'text-sm',
      brandText: 'text-lg',
      tagline: 'text-xs'
    },
    md: {
      mark: 'w-9 h-9',
      markText: 'text-base',
      brandText: 'text-xl',
      tagline: 'text-sm'
    },
    lg: {
      mark: 'w-12 h-12',
      markText: 'text-xl',
      brandText: 'text-3xl',
      tagline: 'text-base'
    },
    xl: {
      mark: 'w-16 h-16',
      markText: 'text-2xl',
      brandText: 'text-4xl',
      tagline: 'text-lg'
    }
  };

  const currentSize = sizes[size] || sizes.md;

  // RW Brand Mark Component
  const BrandMark = ({ markSize, textSize }) => (
    <div 
      className={`${markSize} rounded-lg bg-primary flex items-center justify-center flex-shrink-0`}
      data-testid="rw-brand-mark"
    >
      <span className={`font-bold text-primary-foreground ${textSize}`}>
        RW
      </span>
    </div>
  );

  // Full Logo Content
  const LogoContent = () => {
    if (variant === 'mark') {
      return <BrandMark markSize={currentSize.mark} textSize={currentSize.markText} />;
    }

    return (
      <div className="flex items-center gap-2">
        <BrandMark markSize={currentSize.mark} textSize={currentSize.markText} />
        <div className="flex flex-col">
          <span 
            className={`font-bold text-primary leading-tight ${currentSize.brandText}`}
            data-testid="logo-text"
          >
            RanchWorks
          </span>
          {(variant === 'full' || showTagline) && (
            <span 
              className={`text-muted-foreground leading-tight ${currentSize.tagline}`}
              data-testid="logo-tagline"
            >
              Book what your ranch needs
            </span>
          )}
        </div>
      </div>
    );
  };

  // If onClick is provided, use a button/div instead of Link
  if (onClick) {
    return (
      <button 
        onClick={onClick}
        className={`inline-flex items-center focus:outline-none ${className}`}
        data-testid="logo-container"
      >
        <LogoContent />
      </button>
    );
  }

  // If linkTo is null, just render without link
  if (linkTo === null) {
    return (
      <div className={`inline-flex items-center ${className}`} data-testid="logo-container">
        <LogoContent />
      </div>
    );
  }

  return (
    <Link 
      to={linkTo} 
      className={`inline-flex items-center hover:opacity-90 transition-opacity ${className}`}
      data-testid="logo-container"
    >
      <LogoContent />
    </Link>
  );
}

/**
 * LogoMark - Just the RW brand mark for app icons
 */
export function LogoMark({ size = 'md', className = '' }) {
  return <Logo variant="mark" size={size} className={className} linkTo={null} />;
}

/**
 * LogoFull - Full logo with tagline
 */
export function LogoFull({ size = 'md', className = '', linkTo = '/' }) {
  return <Logo variant="full" size={size} className={className} linkTo={linkTo} />;
}

/**
 * LogoCompact - Logo without tagline
 */
export function LogoCompact({ size = 'md', className = '', linkTo = '/' }) {
  return <Logo variant="compact" size={size} className={className} linkTo={linkTo} />;
}

export default Logo;
